<template>
    <div class="comment-list-container-vote">
        <Comments
            ref="commentEl"
            sourceFrom="VOTE"
            :isLoading="loading"
            :commentList="commentsList"
            :permission="permission"
            :currentUser="currentUser"
            :maxLength="1000"
            :timeZone="timeZone"
            :showCommentTypeSelect="false"
            :withSpaceId="spaceId"
            :patentId="patentId"
            :isPersonalAccount="!currentUser.company_id"
            @delete="isDeleteConfirmDisplay = true; deleteData = $event;"
            @submit="submitComment"
            @cancel="cancel"
        />
        <DeleteCommentConfirm
            v-if="isDeleteConfirmDisplay"
            :onActionHandler="deleteComment"
            :onCloseHandler="() => isDeleteConfirmDisplay = false"
        />
    </div>
</template>

<script>
import _ from 'lodash';
import Comments from '@/presentation/components/CommentsWithRightAvatar.vue';
import DeleteCommentConfirm from './DeleteCommentConfirm.vue';
import { Toast } from 'patsnap-biz';
import { addCommentApi, deleteCommentsFromSpaceApi, updateCommentApi } from 'library-@/apis/workspace_api/comments';
import { getVoteCommentsInfo } from 'library-@/apis/workspace_api/vote';

export default {
    name: 'CommentsList',
    components: {
        DeleteCommentConfirm,
        Comments,
    },
    inject: ['fieldEditorService'],
    props: {
        spaceId: {
            required: true,
            type: String,
        },
        folderId: {
            required: true,
            type: String,
        },
        patentId: {
            required: true,
            type: String,
        },
        fieldId: {
            required: false,
            type: String,
        },
        comments: {
            type: Array,
            default() {
                return [];
            },
        },
        timeZone: {
            type: [Number, String],
            default() {
                const utcOffset = (new Date()).getTimezoneOffset();
                return utcOffset / 60;
            },
        },
        currentUser: {
            type: Object,
            required: true,
            validator(u) {
                return 'user_id' in u;
            },
        },
        permission: {
            type: Object,
            required: true,
            validator(o) {
                return 'isOwner' in o;
            },
        },
    },
    data() {
        return {
            loading: false,
            isDeleteConfirmDisplay: false,
            deleteData: null,
            commentsList: [],
        };
    },
    watch: {
        comments: {
            handler(comments) {
                if (comments && this.fieldEditorService && this.fieldEditorService.voteStatus.enabled) {
                    this.commentsList = _.cloneDeep(this.comments);
                } else {
                    this.getCommentsList();
                }
            },
            deep: true,
        },
    },
    created() {
        if (this.comments) {
            this.commentsList = _.cloneDeep(this.comments);
        } else {
            this.getCommentsList();
        }
    },
    methods: {
        getCommentsList() {
            this.loading = true;
            if (this.fieldEditorService) {
                const params = {
                    folder_id: this.folderId,
                    data_id: this.patentId,
                    field_id: this.fieldId,
                    data_type: 'PATENT',
                };
                return getVoteCommentsInfo(params)
                    .then(data => {
                        this.fieldEditorService.setVoteStatusByResp(data);
                    })
                    .catch(error => {
                        this.showError(error);
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            }
        },
        submitComment({ type, data }) {
            this.loading = true;

            const action = type === 'add' ? addCommentApi : updateCommentApi;

            action({
                ...data,
                folder_id: this.folderId,
                data_id: this.patentId,
                field_id: this.fieldEditorService ? this.fieldId : null,
            }).then(({ comment_id }) => {
                this.getCommentsList()
                    .then(() => {
                        this.$emit('comment-action-event', { type, data, commentsList: this.commentsList });
                        this.$nextTick(() => {
                            this.scrollToComment(comment_id);
                        });
                    });
            }).catch((error) => {
                this.loading = false;
                this.showError(error);
            });
        },
        deleteComment() {
            this.isDeleteConfirmDisplay = false;
            this.loading = true;
            const data = this.deleteData;

            deleteCommentsFromSpaceApi({
                ...data,
                space_id: this.spaceId,
                folder_id: this.folderId,
                data_id: this.patentId,
            }).then(() => {
                this.getCommentsList()
                    .then(() => {
                        this.$emit('comment-action-event', { type: 'delete', data, commentsList: this.commentsList });
                    });
            }).catch((error) => {
                this.loading = false;
                this.showError(error);
            });
        },
        cancel() {
            if (!_.get(this, 'commentsList.length')) {
                this.$emit('close');
            }
        },
        showError(error = {}) {
            const message = error._error_msg;
            if (!message) return;
            Toast({
                message,
                type: 'error',
                showClose: true,
            });
        },
        scrollToComment(id) {
            const commentEl = _.get(this, '$refs.commentEl');
            if (commentEl) {
                commentEl.scrollToCommentById(id);
            }
        },
    },

};
</script>

<style lang="scss">
.comment-list-container-vote{
    .comment {
        display: flex;
        flex-direction: column;
        .comment-container {
            padding: 0px !important;
            height: auto !important;
            flex: 1;
            .comment-item__header-relation {
                .comment-to, .comment-by {
                    max-width: 40%;
                }
            }
        }
        .comment-container__no-data {
            flex: 1;
            overflow: hidden;
            img {
                height: calc(100% - 40px);
                max-height: 200px;
                width: unset;
                margin: 8px 0;
            }
        }
    }
    .comment-footer {
        padding: 16px 8px 16px 0;
    }
    textarea {
      border-color: #d2d2d2;
    }
    .auto-height {
        height: auto !important;
    }

    .patsnap-ui-button--default{
        background-color: $brand-primary !important;
        color: $bg-white !important;
        border: none !important;;
        &:hover {
            background-color: #3d921d !important;
        }
        &[disabled],
        &[disabled]:active,
        &[disabled]:focus,
        &[disabled]:hover{
            background-color: #c6e3bb !important;
            cursor: not-allowed !important;
        }
    }

    .patsnap-ui-button--alternative{
        border: none !important;
        background-color: $bg-dark !important;
        color: #505f79 !important;
        &:hover {
            background-color: $border-light !important;
        }
    }

    .comment-footer__container{
        .patsnap-ui-input__inner {
            line-height: 24px !important;
        }
    }
}

</style>
